import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCanvasStore } from "@/store/useCanvasStore";
import { THEME } from "@/config/theme";
import type { KFNodeData, CanvasShape } from "@/store/useCanvasStore";

// Reveal/present HUD — restyled to the design mock (bucket 2): a rounded pill
// with square nav buttons, a step counter, amber step dots, and an Exit button.
// Behavior is unchanged (the "or click to reveal" interaction hint is bucket 3).
export default function RevealHUD() {
  const { revealPointer, rfNodes, rfShapes, advanceReveal, retreatReveal, exitRevealMode } = useCanvasStore();

  const maxStep = Math.max(
    0,
    ...rfNodes.map(n => (n.data as KFNodeData).revealStep),
    ...rfShapes.map(n => (n.data as unknown as CanvasShape).revealStep),
  );

  const navClass =
    "w-8 h-8 flex items-center justify-center rounded-[9px] bg-zinc-800 text-zinc-200 " +
    "hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-zinc-800 transition-colors";

  return (
    <div
      data-testid="reveal-hud"
      className="flex items-center gap-2.5 bg-zinc-900 border border-white/10 rounded-[14px] pl-3 pr-2.5 py-2 shadow-lg shadow-black/40 select-none"
    >
      <button
        data-testid="reveal-retreat"
        onClick={retreatReveal}
        disabled={revealPointer === 0}
        className={navClass}
        title="Previous step (←)"
      >
        <ChevronLeft size={16} />
      </button>

      <div className="flex flex-col items-center gap-1.5 min-w-[4.5rem]">
        <span className="text-zinc-100 text-xs font-semibold tabular-nums text-center">
          {revealPointer} / {maxStep}
        </span>
        {maxStep > 0 && (
          <div className="flex gap-1">
            {Array.from({ length: maxStep }, (_, i) => {
              const filled = i + 1 <= revealPointer;
              return (
                <span
                  key={i}
                  data-testid="reveal-dot"
                  data-filled={filled}
                  className="w-[7px] h-[7px] rounded-full transition-all"
                  style={{
                    background: filled ? THEME.raw.revealDotLit : THEME.raw.revealDotDim,
                    boxShadow: filled ? THEME.raw.revealDotGlow : "none",
                  }}
                />
              );
            })}
          </div>
        )}
      </div>

      <button
        data-testid="reveal-advance"
        onClick={advanceReveal}
        disabled={revealPointer >= maxStep}
        className={navClass}
        title="Next step (Space / →)"
      >
        <ChevronRight size={16} />
      </button>

      <div className="w-px h-5 bg-zinc-700 mx-0.5" />

      {/* Interaction hint (bucket 3 — matches the new click-to-advance flow). */}
      <div className="hidden sm:flex items-center gap-1.5 text-[10.5px] font-mono text-zinc-500">
        <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 not-italic">Space</kbd>
        or click to reveal
      </div>

      <button
        data-testid="reveal-exit"
        onClick={exitRevealMode}
        className="px-3 py-1.5 rounded-[9px] border border-zinc-700 text-zinc-400 hover:text-zinc-200 text-xs font-semibold transition-colors"
        title="Exit (Esc)"
      >
        Exit
      </button>
    </div>
  );
}
